import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  Plus, Trash2, X, ChevronDown, ChevronRight,
  Loader2, CheckCircle2, AlertCircle, Search, Save,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import type { AppBoom } from '../../types/planManejo';
import type { EmpFinca, EmpLote, EmpBloque } from '../../types/empacadora';
import {
  calcularBoleta, calcularAguaRequerida,
} from '../../types/planManejo';
import {
  inputCls, selectCls, labelCls, btnPrimary, btnSecondary,
  pageTitleCls, pageSubtitleCls,
} from '../../components/ui';
import { imprimirBoletas } from './boletaImprimir';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toISO(d: Date) { return d.toISOString().slice(0, 10); }

function fmtNum(v: number | null | undefined, dec = 3) {
  if (v == null) return '—';
  return v.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

function padBoleta(n: number) { return String(n).padStart(10, '0'); }

function naturalCompare(a: string | number | null | undefined, b: string | number | null | undefined) {
  return String(a ?? '').localeCompare(String(b ?? ''), 'es-CR', {
    numeric: true,
    sensitivity: 'base',
  });
}

function semanaLabel(fecha: Date): string {
  const tmp = new Date(Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  const yy = String(fecha.getFullYear()).slice(-2);
  return `${String(week).padStart(2, '0')}-${yy}`;
}

function semanaAplicacion(fecha: Date): string {
  const d = new Date(fecha);
  if (d.getDay() === 0) d.setDate(d.getDate() + 1);
  return semanaLabel(d);
}

function domingoDeSemana(fecha: Date): Date {
  const d = new Date(fecha);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

function sabadoDeFecha(fecha: Date): Date {
  const d = domingoDeSemana(fecha);
  d.setDate(d.getDate() + 6);
  return d;
}

// ─── Tipos internos ───────────────────────────────────────────────────────────

/**
 * Aplicación del catálogo de Boleta Manual (app_aplicaciones_manuales).
 * Reemplaza el uso de app_plan_etapas: la Boleta Manual es solo para
 * aplicaciones fuera de cédula y sin fecha ancla.
 */
interface AplicacionManual {
  id: number;
  nombre: string;
  orden: number;
  tratamiento_default?: string | null;
  receta_productos?: Partial<FilaProducto>[] | null;
}

interface InvProducto {
  codigo: string;       // codigo_vfp (= codigo_erp)
  nombre: string;
  empaque: string;      // unidad de medida del artículo agro
  ingrediente_activo?: string;
  departamento?: string;
  latencia?: string;
  reingreso?: string;
  inv_producto_id?: number | null;  // id en inv_productos (enlace inventario)
}

interface FilaProducto {
  key: string;                  // uuid local para React key
  articulo: string;
  inv_producto_id: number | null;  // enlace a inventario ERP (validar saldo)
  nombre_producto: string;
  medida: string;
  grupo_quimico: string;
  descripcion: string;
  latencia: string;
  reingreso: string;
  dosis: string;                // input del usuario (string para edición)
  // calculados
  cantidad_aplicar: number;
  cantidad_boom: number;
  cantidad_fraccion: number;
}

interface LoteSeleccion {
  lote: EmpLote;
  bloques: EmpBloque[];         // bloques seleccionados (tipo siembra)
}

// ─── Modal genérico ───────────────────────────────────────────────────────────

function Modal({ title, onClose, children, wide = false }: {
  title: string; onClose: () => void; children: React.ReactNode; wide?: boolean;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="relative w-full flex flex-col rounded-xl shadow-2xl overflow-hidden"
        style={{
          maxWidth: wide ? '56rem' : '36rem', maxHeight: '90vh',
          background: 'var(--surface-overlay)', border: '1px solid var(--line)',
        }}>
        <div className="flex items-center justify-between px-5 py-3.5 shrink-0"
          style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
          <span className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{title}</span>
          <button onClick={onClose} style={{ color: 'var(--ink-faint)' }}><X size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}

// ─── Modal selección Lote / Bloques ──────────────────────────────────────────

function ModalLoteBloques({ onConfirm, onClose, empresaId }: {
  onConfirm: (sel: LoteSeleccion) => void;
  onClose: () => void;
  empresaId: number;
}) {
  const [fincas, setFincas] = useState<EmpFinca[]>([]);
  const [lotes, setLotes] = useState<EmpLote[]>([]);
  const [bloques, setBloques] = useState<EmpBloque[]>([]);
  const [loading, setLoading] = useState(true);

  const [fincaId, setFincaId] = useState('');
  const [loteId, setLoteId] = useState('');
  const [marcados, setMarcados] = useState<Set<string>>(new Set());

  useEffect(() => {
    async function cargar() {
      setLoading(true);
      const [{ data: f }, { data: l }, { data: b }] = await Promise.all([
        supabase.from('emp_fincas').select('id, nombre').eq('empresa_id', empresaId).eq('activo', true).order('nombre'),
        supabase.from('emp_lotes').select('*, finca:emp_fincas(nombre)').eq('empresa_id', empresaId).eq('activo', true).order('numero'),
        // Sin filtrar por fecha_siembra: los bloques en Preparación (antes de
        // sembrar) todavía no la tienen, pero igual necesitan boletas manuales
        // (quemas, sello) — excluirlos aquí los dejaba sin poder pasar cédula.
        supabase.from('emp_bloques').select('*').eq('empresa_id', empresaId).eq('tipo', 'siembra').eq('activo', true).order('num'),
      ]);
      setFincas((f ?? []) as EmpFinca[]);
      setLotes((l ?? []) as EmpLote[]);
      setBloques((b ?? []) as EmpBloque[]);
      setLoading(false);
    }
    cargar();
  }, [empresaId]);

  const lotesF = fincaId ? lotes.filter(l => l.finca_id === fincaId) : lotes;
  const loteActual = lotes.find(l => l.id === loteId);
  const bloquesLote = bloques
    .filter(b => b.lote_id === loteId)
    .sort((a, b) => naturalCompare(a.num, b.num));

  function toggleBloque(id: string) {
    setMarcados(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  }

  function toggleTodos() {
    if (marcados.size === bloquesLote.length) {
      setMarcados(new Set());
    } else {
      setMarcados(new Set(bloquesLote.map(b => b.id)));
    }
  }

  function confirmar() {
    if (!loteActual) return;
    const sel = bloquesLote.filter(b => marcados.has(b.id)).sort((a, b) => naturalCompare(a.num, b.num));
    if (sel.length === 0) return;
    onConfirm({ lote: loteActual, bloques: sel });
  }

  return (
    <Modal title="Selección de Lote y Bloques" onClose={onClose} wide>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin" style={{ color: 'var(--accent-main)' }} /></div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Finca */}
          <div>
            <label className={labelCls}>Finca</label>
            <select className={selectCls} value={fincaId} onChange={e => { setFincaId(e.target.value); setLoteId(''); setMarcados(new Set()); }}>
              <option value="">— Todas las fincas —</option>
              {fincas.map(f => <option key={f.id} value={f.id}>{f.nombre}</option>)}
            </select>
          </div>

          {/* Lote */}
          <div>
            <label className={labelCls}>Lote</label>
            <select className={selectCls} value={loteId} onChange={e => { setLoteId(e.target.value); setMarcados(new Set()); }}>
              <option value="">— Seleccionar lote —</option>
              {lotesF.map(l => (
                <option key={l.id} value={l.id}>
                  {l.numero} — {l.nombre} {l.finca ? `(${(l.finca as any).nombre})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Bloques */}
          {loteId && (
            <div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
            <label className={labelCls}>Bloques de siembra</label>
            <button className="text-[11px]" style={{ color: 'var(--accent-main)' }} onClick={toggleTodos}>
              {marcados.size === bloquesLote.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                </button>
              </div>
              {bloquesLote.length === 0 ? (
                <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>Sin bloques de siembra con fecha asignada.</p>
              ) : (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
                  {bloquesLote.map(b => (
                    <label key={b.id} className="flex items-center gap-2 p-2 rounded cursor-pointer text-xs"
                      style={{
                        background: marcados.has(b.id) ? 'var(--accent-soft)' : 'var(--surface-raised)',
                        border: `1px solid ${marcados.has(b.id) ? 'var(--accent-main)' : 'var(--line)'}`,
                        color: 'var(--ink)',
                      }}>
                      <input type="checkbox" checked={marcados.has(b.id)} onChange={() => toggleBloque(b.id)} className="accent-current" />
                      <span className="font-medium">Bloque {b.num}</span>
                      <span style={{ color: 'var(--ink-faint)' }}>{b.area_ha ? `${b.area_ha.toFixed(2)} ha` : ''}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Resumen selección */}
          {marcados.size > 0 && (() => {
            const sel = bloquesLote.filter(b => marcados.has(b.id));
            const area = sel.reduce((s, b) => s + (b.area_ha ?? 0), 0);
            const plantas = sel.reduce((s, b) => s + (b.plant_count ?? 0), 0);
            return (
              <div className="p-3 rounded text-xs flex flex-wrap gap-3 sm:gap-6" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
                <span><b style={{ color: 'var(--ink)' }}>{sel.length}</b> bloques</span>
                <span><b style={{ color: 'var(--ink)' }}>{area.toFixed(2)}</b> ha</span>
                <span><b style={{ color: 'var(--ink)' }}>{plantas.toLocaleString('es-CR')}</b> plantas</span>
              </div>
            );
          })()}

          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
            <button className={btnSecondary} onClick={onClose}>Cancelar</button>
            <button className={btnPrimary} disabled={marcados.size === 0 || !loteId} onClick={confirmar}>
              Confirmar selección
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── Modal búsqueda de producto ──────────────────────────────────────────────

function ModalBuscarProducto({ onSelect, onClose, empresaId }: {
  onSelect: (p: InvProducto) => void;
  onClose: () => void;
  empresaId: number;
}) {
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState<InvProducto[]>([]);
  const [buscando, setBuscando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const buscar = useCallback(async (texto: string) => {
    if (texto.trim().length < 2) { setResultados([]); return; }
    setBuscando(true);
    // Buscar en inv_productos (misma fuente que Plan de Manejo)
    // y cruzar con app_articulos_agro para obtener reingreso/latencia
    const { data: bodegaFinca } = await supabase
      .from('inv_bodegas')
      .select('id')
      .eq('empresa_id', empresaId)
      .ilike('nombre', 'Finca')
      .eq('activo', true)
      .maybeSingle();
    let productosQuery = supabase
      .from('inv_productos')
      .select('id, codigo, descripcion, unidad_medida')
      .eq('empresa_id', empresaId)
      .eq('activo', true)
      .or(`descripcion.ilike.%${texto}%,codigo.ilike.%${texto}%`)
      .order('descripcion')
      .limit(30);
    productosQuery = bodegaFinca?.id
      ? productosQuery.or(`bodega_id.eq.${bodegaFinca.id},vfp_bodega.eq.1`)
      : productosQuery.eq('vfp_bodega', '1');
    const { data: erpData, error } = await productosQuery;
    if (error || !erpData?.length) { setResultados([]); setBuscando(false); return; }


    // Cruzar con app_articulos_agro para reingreso/latencia/empaque agro
    const codigos = erpData.map((r: any) => r.codigo).filter(Boolean);
    const { data: agroData } = await supabase
      .from('app_articulos_agro')
      .select('codigo_erp, empaque, ingrediente_activo, latencia, reingreso')
      .in('codigo_erp', codigos);
    const agroMap = new Map((agroData ?? []).map((r: any) => [r.codigo_erp, r]));

    const mapped = erpData.map((r: any) => {
      const agro = agroMap.get(r.codigo) as any;
      return {
        codigo: r.codigo ?? '',
        nombre: r.descripcion ?? '',
        empaque: agro?.empaque || r.unidad_medida || '',
        ingrediente_activo: agro?.ingrediente_activo ?? '',
        departamento: '',
        latencia: agro?.latencia ?? '',
        reingreso: agro?.reingreso ?? '',
        inv_producto_id: r.id ?? null,   // enlace a inventario (validar saldo en Bodega)
      };
    });
    setResultados(mapped);
    setBuscando(false);
  }, [empresaId]);

  useEffect(() => {
    const t = setTimeout(() => buscar(q), 300);
    return () => clearTimeout(t);
  }, [q, buscar]);

  return (
    <Modal title="Buscar Producto" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-faint)' }} />
          <input
            ref={inputRef}
            className={inputCls + ' pl-8'}
            placeholder="Código o nombre..."
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>

        {buscando && <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin" style={{ color: 'var(--accent-main)' }} /></div>}

        {!buscando && resultados.length === 0 && q.length >= 2 && (
          <p className="text-xs text-center py-4" style={{ color: 'var(--ink-faint)' }}>Sin resultados para "{q}"</p>
        )}

        <div className="flex flex-col gap-1">
          {resultados.map(p => (
            <button key={p.codigo}
              className="flex items-start gap-3 px-3 py-2.5 rounded text-left transition-colors"
              style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--accent-main)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--line)')}
              onClick={() => onSelect(p)}>
              <span className="font-mono text-xs font-semibold shrink-0 mt-0.5" style={{ color: 'var(--accent-main)', width: 60 }}>{p.codigo}</span>
              <div>
                <div className="text-xs font-medium" style={{ color: 'var(--ink)' }}>{p.nombre}</div>
                <div className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                  {p.empaque}{p.ingrediente_activo ? ` · ${p.ingrediente_activo}` : ''}{p.reingreso ? ` · Reing: ${p.reingreso}` : ''}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ─── Página Principal ─────────────────────────────────────────────────────────

export function BoletaManualPage() {
  const empresaId = useEmpresaId();

  // Empresa
  const [empresaNombre, setEmpresaNombre] = useState('');
  const [empresaCedula, setEmpresaCedula] = useState('');
  const [empresaLogo, setEmpresaLogo] = useState('');

  // Catálogos
  // Aplicaciones de la Boleta Manual: catálogo propio (app_aplicaciones_manuales).
  // NO son etapas del plan de manejo — son las que no se hacen en cédula y no
  // están amarradas a fecha ancla. Administrable en Config → Aplicaciones Manuales.
  const [aplicaciones, setAplicaciones] = useState<AplicacionManual[]>([]);
  const [booms, setBooms] = useState<AppBoom[]>([]);

  // Encabezado boleta
  const hoy = new Date();
  const [aplicacionId, setAplicacionId] = useState('');
  const [boomId, setBoomId] = useState('');
  const [fechaAplica, setFechaAplica] = useState(toISO(hoy));
  const [volumen, setVolumen] = useState('');      // agua_l por boom (editable)
  const [tratamiento, setTratamiento] = useState('');

  // Lote / Bloques
  const [seleccion, setSeleccion] = useState<LoteSeleccion | null>(null);
  const [showModalLote, setShowModalLote] = useState(false);

  // Productos
  const [filas, setFilas] = useState<FilaProducto[]>([]);
  const [showModalProducto, setShowModalProducto] = useState(false);

  // Estado
  const [guardando, setGuardando] = useState(false);
  const [guardandoReceta, setGuardandoReceta] = useState(false);
  const [ok, setOk] = useState('');
  const [error, setError] = useState('');

  // ── Cargar catálogos + empresa ────────────────────────────────────────────

  useEffect(() => {
    async function cargar() {
      const [{ data: aps }, { data: bms }, { data: emp }, { data: cfg }] = await Promise.all([
        supabase.from('app_aplicaciones_manuales').select('id, nombre, orden, tratamiento_default, receta_productos')
          .eq('empresa_id', empresaId).eq('activo', true).order('orden'),
        supabase.from('app_booms').select('*').eq('empresa_id', empresaId).eq('activo', true).order('nombre'),
        supabase.from('empresas').select('nombre, cedula').eq('id', empresaId).maybeSingle(),
        supabase.from('fe_config_empresa').select('logo_url').eq('empresa_id', empresaId).maybeSingle(),
      ]);
      setAplicaciones((aps ?? []) as AplicacionManual[]);
      setBooms((bms ?? []) as AppBoom[]);
      if (emp) {
        setEmpresaNombre((emp as any).nombre ?? '');
        setEmpresaCedula((emp as any).cedula ?? '');
      }
      setEmpresaLogo((cfg as any)?.logo_url ?? '');
    }
    cargar();
  }, [empresaId]);

  // ── Derivados ────────────────────────────────────────────────────────────

  const fechaAplicacion = new Date(fechaAplica + 'T00:00:00');
  const semana = semanaAplicacion(fechaAplicacion);
  const boomActual = booms.find(b => String(b.id) === boomId);
  const boomCapacidad = boomActual?.capacidad_l ?? 0;
  const aguaL = Number(volumen) || 0;

  const areaTotal = seleccion
    ? seleccion.bloques.reduce((s, b) => s + (b.area_ha ?? 0), 0)
    : 0;
  const plantasTotal = seleccion
    ? seleccion.bloques.reduce((s, b) => s + (b.plant_count ?? 0), 0)
    : 0;

  const calcBoom = (boomCapacidad > 0 && aguaL > 0 && areaTotal > 0)
    ? calcularBoleta(areaTotal, aguaL, boomCapacidad)
    : { n_booms_completos: 0, litros_fraccion: 0, agua_total_l: aguaL * areaTotal };

  // Fórmula VFP para boleta manual:
  //   cantidad_aplicar  = dosis × area
  //   cantidad_boom     = (dosis / agua_l) × boom_capacidad, solo si el agua
  //                       total alcanza para un boom completo
  //   cantidad_fraccion = MOD(cantidad_aplicar, cantidad_boom_de_referencia)
  function calcManual(dosis: number, area: number, cap: number, agua: number) {
    const cantidad_aplicar  = dosis * area;
    const ratio             = agua > 0 ? dosis / agua : 0;
    const agua_total        = agua * area;
    // Tasa por UN boom completo, para el cálculo de la fracción.
    const cantidad_por_boom = cap > 0 ? ratio * cap : 0;
    // cantidad_boom solo se llena si el agua total alcanza para un boom completo.
    const cantidad_boom     = agua_total >= cap ? cantidad_por_boom : 0;
    // Fracción vía cantidad_por_boom (no cantidad_boom): sin boom completo da
    // directamente cantidad_aplicar — toda la dosis va en la tanqueada parcial.
    const cantidad_fraccion = cantidad_por_boom > 0 ? cantidad_aplicar % cantidad_por_boom : 0;
    return { cantidad_aplicar, cantidad_boom, cantidad_fraccion };
  }

  function recalcularFilas(rows: FilaProducto[], area: number, cap: number, agua: number): FilaProducto[] {
    return rows.map(f => {
      const dosis = Number(f.dosis) || 0;
      const calc = calcManual(dosis, area, cap, agua);
      return { ...f, ...calc };
    });
  }

  function onChangeBoom(id: string) {
    setBoomId(id);
  }

  function seleccionarAplicacion(id: string) {
    setAplicacionId(id);
    const app = aplicaciones.find(a => String(a.id) === id);
    if (!app) { setTratamiento(''); setFilas([]); return; }
    setTratamiento(app.tratamiento_default?.trim() || app.nombre);
    const receta = Array.isArray(app.receta_productos) ? app.receta_productos : [];
    setFilas(recalcularFilas(receta.map((p: any) => ({
      key: crypto.randomUUID(), articulo: p.articulo ?? '',
      inv_producto_id: p.inv_producto_id ?? null, nombre_producto: p.nombre_producto ?? '',
      medida: p.medida ?? '', grupo_quimico: p.grupo_quimico ?? '', descripcion: p.descripcion ?? '',
      latencia: p.latencia ?? '', reingreso: p.reingreso ?? '', dosis: String(p.dosis ?? ''),
      cantidad_aplicar: 0, cantidad_boom: 0, cantidad_fraccion: 0,
    })), areaTotal, boomCapacidad, aguaL));
  }

  async function guardarRecetaActual() {
    if (!aplicacionId) { setError('Seleccionar una aplicación para guardar la receta.'); return; }
    if (!tratamiento.trim()) { setError('Ingresar el nombre del tratamiento.'); return; }
    if (!filas.length || filas.some(f => !f.dosis || Number(f.dosis) <= 0)) {
      setError('La receta debe tener productos con dosis mayor a 0.'); return;
    }
    setGuardandoReceta(true); setError(''); setOk('');
    const receta_productos = filas.map(({ key, cantidad_aplicar, cantidad_boom, cantidad_fraccion, ...f }) => f);
    const { error: e } = await supabase.from('app_aplicaciones_manuales').update({
      tratamiento_default: tratamiento.trim(), receta_productos,
    }).eq('id', Number(aplicacionId)).eq('empresa_id', empresaId);
    setGuardandoReceta(false);
    if (e) { setError(`No se pudo guardar la receta: ${e.message}`); return; }
    setAplicaciones(prev => prev.map(a => String(a.id) === aplicacionId
      ? { ...a, tratamiento_default: tratamiento.trim(), receta_productos } : a));
    setOk('Receta guardada. Se cargará automáticamente en las próximas boletas.');
  }

  // Al cambiar volumen o área, recalcular filas
  useEffect(() => {
    setFilas(prev => recalcularFilas(prev, areaTotal, boomCapacidad, aguaL));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaTotal, boomCapacidad, aguaL]);

  // ── Agregar producto desde búsqueda ──────────────────────────────────────

  function agregarProducto(p: InvProducto) {
    setShowModalProducto(false);
    // No duplicar
    if (filas.some(f => f.articulo === p.codigo)) return;
    const nuevaFila: FilaProducto = {
      key: crypto.randomUUID(),
      articulo: p.codigo,
      inv_producto_id: p.inv_producto_id ?? null,
      nombre_producto: p.nombre,
      medida: p.empaque,
      grupo_quimico: p.departamento ?? '',
      descripcion: p.ingrediente_activo ?? '',
      latencia: p.latencia ?? '',
      reingreso: p.reingreso ?? '',
      dosis: '',
      cantidad_aplicar: 0,
      cantidad_boom: 0,
      cantidad_fraccion: 0,
    };
    setFilas(prev => [...prev, nuevaFila]);
  }

  // ── Editar dosis ─────────────────────────────────────────────────────────

  function editarDosis(key: string, valor: string) {
    setFilas(prev => prev.map(f => {
      if (f.key !== key) return f;
      const dosis = Number(valor) || 0;
      const calc = calcManual(dosis, areaTotal, boomCapacidad, aguaL);
      return { ...f, dosis: valor, ...calc };
    }));
  }

  function eliminarFila(key: string) {
    setFilas(prev => prev.filter(f => f.key !== key));
  }

  // ── Guardar boleta ────────────────────────────────────────────────────────

  async function guardar() {
    setError(''); setOk('');
    if (!aplicacionId) { setError('Seleccionar una aplicación.'); return; }
    if (!boomId)  { setError('Seleccionar un boom.'); return; }
    if (!seleccion) { setError('Seleccionar lote y bloques.'); return; }
    if (filas.length === 0) { setError('Agregar al menos un producto.'); return; }
    if (filas.some(f => !f.dosis || Number(f.dosis) <= 0)) { setError('Todas las filas deben tener dosis mayor a 0.'); return; }

    setGuardando(true);
    try {
      // Secuencial
      // Leer el máximo número real de boletas para evitar duplicados
      const { data: maxData } = await supabase
        .from('app_boletas')
        .select('numero')
        .eq('empresa_id', empresaId)
        .order('numero', { ascending: false })
        .limit(1)
        .maybeSingle();
      const maxNum = maxData?.numero ? parseInt(maxData.numero, 10) : 0;
      const nextNum = maxNum + 1;

      // Validar enlaces a inventario ANTES de crear la boleta: una receta guardada
      // puede apuntar a un producto que ya no existe (recodificado o depurado).
      // Se re-resuelve por código; si tampoco existe, se avisa sin guardar nada.
      const idsInv = [...new Set(filas.map(f => f.inv_producto_id).filter((v): v is number => !!v))];
      const { data: invExistentes } = idsInv.length
        ? await supabase.from('inv_productos').select('id').eq('empresa_id', empresaId).in('id', idsInv)
        : { data: [] as any[] };
      const idsValidos = new Set((invExistentes ?? []).map((r: any) => r.id));
      const huerfanas = filas.filter(f => f.inv_producto_id && !idsValidos.has(f.inv_producto_id));
      if (huerfanas.length) {
        const { data: porCodigo } = await supabase.from('inv_productos').select('id, codigo')
          .eq('empresa_id', empresaId).in('codigo', huerfanas.map(f => f.articulo));
        const idPorCodigo = new Map((porCodigo ?? []).map((r: any) => [r.codigo, r.id]));
        const sinResolver = huerfanas.filter(f => !idPorCodigo.has(f.articulo));
        if (sinResolver.length) {
          throw new Error(`Estos productos ya no existen en inventario: ${sinResolver.map(f => f.nombre_producto).join(', ')}. Quítelos y agréguelos de nuevo con "Agregar producto".`);
        }
        huerfanas.forEach(f => { f.inv_producto_id = idPorCodigo.get(f.articulo) ?? null; });
      }

      const aplicacion = aplicaciones.find(a => String(a.id) === aplicacionId);
      const domingo = domingoDeSemana(fechaAplicacion);
      const sabado = sabadoDeFecha(fechaAplicacion);
      const bloquesOrdenados = [...seleccion.bloques].sort((a, b) => naturalCompare(a.num, b.num));
      const bloqueNums = bloquesOrdenados.map(b => String(b.num)).join(',');

      // Grupo(s) de forza: lista única de los bloques seleccionados (para RE-08).
      const grupoForza = [...new Set(
        bloquesOrdenados.map(b => (b as any).grupo_forza).filter(Boolean)
      )].sort(naturalCompare).join(', ') || null;

      // Fecha de siembra: del primer bloque con fecha
      const fechaSiembra = bloquesOrdenados.find(b => b.fecha_siembra)?.fecha_siembra;

      const boleta = {
        empresa_id: empresaId,
        numero: padBoleta(nextNum),
        semana,
        fecha_desde: toISO(domingo),
        fecha_hasta: toISO(sabado),
        lote_id: seleccion.lote.id,
        lote_num: seleccion.lote.numero,
        finca_nombre: (seleccion.lote.finca as any)?.nombre ?? '',
        bloques: bloqueNums,
        grupo_forza: grupoForza,
        fecha_siembra: fechaSiembra,
        area_ha: areaTotal,
        plantas: plantasTotal,
        densidad: areaTotal > 0 ? Math.round(plantasTotal / areaTotal) : 0,
        // Catálogo propio de Boleta Manual. etapa_id queda NULL: ya no se
        // registran boletas manuales contra etapas del plan de manejo.
        // etapa_nombre se conserva con el nombre de la aplicación para que los
        // reportes existentes (que agrupan por ese texto) sigan funcionando.
        aplicacion_manual_id: Number(aplicacionId),
        etapa_id: null,
        etapa_nombre: aplicacion?.nombre ?? '',
        ciclo_id: null,
        ciclo_nombre: null,
        boom_id: Number(boomId),
        boom_nombre: boomActual?.nombre ?? null,
        boom_capacidad_l: boomCapacidad,
        agua_l: aguaL,
        n_booms_completos: calcBoom.n_booms_completos,
        litros_fraccion: calcBoom.litros_fraccion,
        agua_total_l: calcBoom.agua_total_l,
        manual: true,
        estado: 'pendiente',
        fecha_plan: fechaAplica,
        tratamiento: tratamiento || null,
      };

      const { data: inserted, error: eIns } = await supabase
        .from('app_boletas')
        .insert([boleta])
        .select('id, numero')
        .single();
      if (eIns) throw eIns;

      const boletaId = inserted.id;

      // Backfill de campos agronómicos requeridos por certificación G.A.P.
      // MISMA cascada que la boleta generada: plan → catálogo (inv_productos) → 'N/A'.
      // inv_productos es la fuente única de justificación/latencia/reingreso/grupo/
      // ingrediente (decisión 24/05). Antes la manual solo miraba el plan y dejaba
      // Justificación / Ingrediente Activo / Intervalo vacíos.
      const articulos = filas.map(f => f.articulo).filter(Boolean);
      const planLookup: Record<string, { justificacion?: string; departamento?: string; latencia?: number | null; grupo_quimico?: string; ingrediente_activo?: string }> = {};
      const invLookup: Record<string, { justificacion?: string; latencia_dias?: any; reingreso?: string; departamento?: string; grupo_quimico?: string; ingrediente_activo?: string; descripcion_detallada?: string; descripcion?: string }> = {};
      if (articulos.length > 0) {
        const { data: planFilas } = await supabase
          .from('app_plan_productos')
          .select('articulo, justificacion, departamento, latencia, grupo_quimico, ingrediente_activo')
          .eq('empresa_id', empresaId)
          .in('articulo', articulos);
        (planFilas || []).forEach((pf: any) => {
          if (pf.articulo && !planLookup[pf.articulo]) {
            planLookup[pf.articulo] = {
              justificacion: pf.justificacion ?? undefined,
              departamento: pf.departamento ?? undefined,
              latencia: pf.latencia ?? null,
              grupo_quimico: pf.grupo_quimico ?? undefined,
              ingrediente_activo: pf.ingrediente_activo ?? undefined,
            };
          }
        });
        const { data: invFilas } = await supabase
          .from('inv_productos')
          .select('codigo, descripcion, descripcion_detallada, justificacion, latencia_dias, reingreso, departamento, grupo_quimico, ingrediente_activo')
          .eq('empresa_id', empresaId)
          .in('codigo', articulos);
        (invFilas || []).forEach((iv: any) => {
          if (iv.codigo && !invLookup[iv.codigo]) {
            invLookup[iv.codigo] = {
              justificacion: iv.justificacion ?? undefined,
              latencia_dias: iv.latencia_dias ?? undefined,
              reingreso: iv.reingreso ?? undefined,
              departamento: iv.departamento ?? undefined,
              grupo_quimico: iv.grupo_quimico ?? undefined,
              ingrediente_activo: iv.ingrediente_activo ?? undefined,
              descripcion_detallada: iv.descripcion_detallada ?? undefined,
              descripcion: iv.descripcion ?? undefined,
            };
          }
        });
      }
      // Helpers idénticos a la boleta generada.
      const blank = (v: any) => v === null || v === undefined || String(v).trim() === '';
      const fmtLatencia = (v: any) => (/^\d+$/.test(String(v).trim()) ? `${v} DIAS` : String(v));

      // Productos — cascada plan → fila (app_articulos_agro) → catálogo (inv_productos) → 'N/A'.
      const prods = filas.map((f, idx) => {
        const pl = planLookup[f.articulo] || {};
        const iv = invLookup[f.articulo] || {};

        // Ingrediente activo (col "Fórmula o Ingrediente Activo"): fila → plan → catálogo.
        const ingrediente = !blank(f.descripcion) ? f.descripcion
          : (!blank(pl.ingrediente_activo) ? pl.ingrediente_activo
          : (!blank(iv.ingrediente_activo) ? iv.ingrediente_activo
          : (!blank(iv.descripcion_detallada) ? iv.descripcion_detallada
          : (!blank(iv.descripcion) ? iv.descripcion : null))));
        // Justificación: plan → catálogo.
        const justif = !blank(pl.justificacion) ? pl.justificacion
          : (!blank(iv.justificacion) ? iv.justificacion : null);
        // Grupo químico: fila → plan → catálogo.
        const grupo = !blank(f.grupo_quimico) ? f.grupo_quimico
          : (!blank(pl.grupo_quimico) ? pl.grupo_quimico
          : (!blank(iv.grupo_quimico) ? iv.grupo_quimico : null));
        const depto = !blank(pl.departamento) ? pl.departamento
          : (!blank(iv.departamento) ? iv.departamento : null);
        // Regla G.A.P.: latencia (Intervalo Cosecha) y reingreso (Período Re-Ing)
        // NUNCA vacíos → 'N/A' si no hay dato en ninguna fuente.
        const latenc = !blank(f.latencia) ? fmtLatencia(f.latencia)
          : (!blank(pl.latencia) ? fmtLatencia(pl.latencia)
          : (!blank(iv.latencia_dias) ? `${iv.latencia_dias} DIAS` : 'N/A'));
        const reing = !blank(f.reingreso) ? f.reingreso
          : (!blank(iv.reingreso) ? iv.reingreso : 'N/A');

        return {
          empresa_id: empresaId,
          boleta_id: boletaId,
          articulo: f.articulo,
          inv_producto_id: f.inv_producto_id ?? null,
          nombre_producto: f.nombre_producto,
          descripcion: ingrediente,
          ingrediente_activo: ingrediente,
          justificacion: justif,
          grupo_quimico: grupo,
          departamento: depto,
          medida: f.medida || null,
          latencia: latenc,
          reingreso: reing,
          orden: idx + 1,
          dosis: Number(f.dosis),
          volumen: aguaL,
          cantidad_aplicar: f.cantidad_aplicar,
          cantidad_boom: f.cantidad_boom,
          cantidad_fraccion: f.cantidad_fraccion,
          es_agua: false,
        };
      });

      // Línea AGUA REQUERIDA
      if (aguaL > 0 && areaTotal > 0) {
        const { id: _id, created_at: _cat, ...aguaBase } = calcularAguaRequerida(aguaL, areaTotal, boomCapacidad || 1);
        prods.push({ ...aguaBase, empresa_id: empresaId, boleta_id: boletaId, orden: 9999 } as any);
      }

      const { error: eProds } = await supabase.from('app_boleta_productos').insert(prods);
      if (eProds) {
        // Sin productos la boleta no sirve: borrar el encabezado para no dejarla vacía.
        await supabase.from('app_boletas').delete().eq('id', boletaId).eq('empresa_id', empresaId);
        throw eProds;
      }

      // Sincronizar secuencial con el número real usado
      await supabase.from('app_boletas_seq').upsert(
        { empresa_id: empresaId, ultimo_num: nextNum },
        { onConflict: 'empresa_id' }
      );

      setOk(`Boleta ${padBoleta(nextNum)} generada correctamente.`);

      // Cédula impresa: se abre el diálogo de impresión al finalizar, igual
      // que en el historial de Cédulas (boletaImprimir.ts).
      try {
        const boletaImpresa = { ...boleta, id: boletaId, numero: inserted.numero };
        await imprimirBoletas([boletaImpresa as any], async () => prods as any, empresaNombre, empresaCedula, empresaLogo);
      } catch {
        // No bloquear el flujo si falla la impresión; la boleta ya quedó guardada.
      }

      // Limpiar formulario
      setAplicacionId('');
      setBoomId('');
      setVolumen('');
      setTratamiento('');
      setSeleccion(null);
      setFilas([]);
    } catch (ex: any) {
      setError(ex?.message ?? 'Error al guardar la boleta.');
    } finally {
      setGuardando(false);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  const aplicacionActual = aplicaciones.find(a => String(a.id) === aplicacionId);

  return (
    <div className="flex flex-col gap-4 p-3 sm:gap-5 sm:p-5 max-w-5xl mx-auto">

      {/* Título */}
      <div>
        <h1 className={pageTitleCls}>Boleta Manual de Aplicación</h1>
        <p className={pageSubtitleCls}>Ingreso manual · semana calculada por fecha de aplicación</p>
      </div>

      {/* Alertas */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded text-sm"
          style={{ background: 'var(--tone-error-bg)', border: '1px solid var(--tone-error-border)', color: 'var(--tone-error-text)' }}>
          <AlertCircle size={15} />{error}
        </div>
      )}
      {ok && (
        <div className="flex items-center gap-2 px-4 py-3 rounded text-sm"
          style={{ background: 'var(--tone-success-bg)', border: '1px solid var(--tone-success-border)', color: 'var(--tone-success-text)' }}>
          <CheckCircle2 size={15} />{ok}
        </div>
      )}

      {/* ── Encabezado ── */}
      <div className="rounded-lg p-3 sm:p-5 flex flex-col gap-4" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
        <p className="text-[11px] font-bold uppercase tracking-widest pb-1.5 border-b"
          style={{ color: 'var(--ink-faint)', borderColor: 'var(--line)' }}>Datos de la Boleta</p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:grid-cols-4">
          {/* Aplicación — catálogo propio (Config → Aplicaciones Manuales) */}
          <div>
            <label className={labelCls}>Aplicación</label>
            <select className={selectCls} value={aplicacionId}
              onChange={e => seleccionarAplicacion(e.target.value)}>
              <option value="">— Seleccionar —</option>
              {aplicaciones.map(a => <option key={a.id} value={String(a.id)}>{a.nombre}</option>)}
            </select>
          </div>

          {/* Fecha */}
          <div>
            <label className={labelCls}>Fecha de Aplicación</label>
            <input type="date" className={inputCls} value={fechaAplica} onChange={e => setFechaAplica(e.target.value)} />
          </div>

          {/* Semana */}
          <div>
            <label className={labelCls}>Semana</label>
            <div className="px-3 py-2 rounded text-sm font-semibold"
              style={{ background: 'var(--surface-overlay)', border: '1px solid var(--line)', color: 'var(--accent-main)' }}>
              {semana}
            </div>
          </div>

          {/* Boom */}
          <div className="sm:col-span-2">
            <label className={labelCls}>Boom (equipo)</label>
            <select className={selectCls} value={boomId} onChange={e => onChangeBoom(e.target.value)}>
              <option value="">— Seleccionar —</option>
              {booms.map(b => <option key={b.id} value={String(b.id)}>{b.nombre} — {b.capacidad_l} L</option>)}
            </select>
          </div>

          {/* Volumen */}
          <div>
            <label className={labelCls}>Agua (L/ha)</label>
            <input type="number" className={inputCls} placeholder="Litros por ha"
              value={volumen} onChange={e => setVolumen(e.target.value)} min={0} />
          </div>

          {/* Tratamiento */}
          <div>
            <label className={labelCls}>Tratamiento</label>
            <input type="text" className={inputCls} placeholder="Texto libre"
              value={tratamiento} onChange={e => setTratamiento(e.target.value)} />
          </div>
        </div>
      </div>

      {/* ── Lote / Bloques ── */}
      <div className="rounded-lg p-3 sm:p-5 flex flex-col gap-3" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--ink-faint)' }}>Lote y Bloques</p>
          <button className={btnSecondary} onClick={() => setShowModalLote(true)}>
            {seleccion ? 'Cambiar selección' : 'Selección Lotes / Bloques'}
          </button>
        </div>

        {seleccion ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4 text-xs">
            <InfoField label="Lote" value={`${seleccion.lote.numero} — ${seleccion.lote.nombre}`} />
            <InfoField label="Finca" value={(seleccion.lote.finca as any)?.nombre ?? '—'} />
            <InfoField label="Bloques" value={seleccion.bloques.map(b => b.num).join(', ')} />
            <InfoField label="Hectáreas" value={areaTotal.toFixed(2)} />
            <InfoField label="Plantas" value={plantasTotal.toLocaleString('es-CR')} />
            <InfoField label="Aplicación" value={aplicacionActual?.nombre ?? '—'} />
            {seleccion.bloques[0]?.fecha_siembra && (
              <InfoField label="Siembra" value={new Date(seleccion.bloques[0].fecha_siembra + 'T00:00:00').toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' })} />
            )}
          </div>
        ) : (
          <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>Sin lote seleccionado.</p>
        )}

        {/* Cálculos boom */}
        {seleccion && aguaL > 0 && boomCapacidad > 0 && (
          <div className="grid grid-cols-1 gap-3 pt-2 border-t sm:grid-cols-3" style={{ borderColor: 'var(--line)' }}>
            <InfoField label="Agua requerida (L)" value={fmtNum(calcBoom.agua_total_l, 0)} accent />
            <InfoField label="Boones completos" value={String(calcBoom.n_booms_completos)} accent />
            <InfoField label="Fracción (L)" value={fmtNum(calcBoom.litros_fraccion, 1)} accent />
          </div>
        )}
      </div>

      {/* ── Productos ── */}
      <div className="rounded-lg p-3 sm:p-5 flex flex-col gap-3" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--ink-faint)' }}>Detalle de Productos</p>
          <div className="flex items-center gap-2">
            <button onClick={guardarRecetaActual} disabled={guardandoReceta || !aplicacionId || filas.length === 0}
              className={btnSecondary} title="Guardar este tratamiento y sus productos como receta reutilizable">
              <Save size={13} className="inline mr-1" />
              {guardandoReceta ? 'Guardando receta…' : 'Guardar como receta'}
            </button>
            <button className={btnSecondary} onClick={() => setShowModalProducto(true)}>
              <Plus size={13} className="inline mr-1" />Agregar producto
            </button>
          </div>
        </div>

        {filas.length === 0 ? (
          <p className="text-xs text-center py-6" style={{ color: 'var(--ink-faint)' }}>
            Sin productos. Use "Agregar producto" para buscar por código o nombre.
          </p>
        ) : (
          <>
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr style={{ background: 'var(--surface-overlay)', borderBottom: '1px solid var(--line)' }}>
                  <Th w={72}>Código</Th>
                  <Th>Artículo</Th>
                  <Th w={56}>Medida</Th>
                  <Th w={90}>Dosis</Th>
                  <Th w={100} right>Boom</Th>
                  <Th w={100} right>Fracción</Th>
                  <Th w={100} right>Aplicar</Th>
                  <Th w={70}>Reingreso</Th>
                  <Th w={60}>Latencia</Th>
                  <Th w={36}></Th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => (
                  <tr key={f.key} style={{ borderBottom: '1px solid var(--line-dim)' }}>
                    <td className="px-3 py-2 font-mono font-semibold" style={{ color: 'var(--accent-main)', width: 72 }}>{f.articulo}</td>
                    <td className="px-3 py-2" style={{ color: 'var(--ink)' }}>
                      <div>{f.nombre_producto}</div>
                      {f.grupo_quimico && <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>{f.grupo_quimico}</div>}
                    </td>
                    <td className="px-3 py-2" style={{ color: 'var(--ink-muted)', width: 56 }}>{f.medida}</td>
                    <td className="px-2 py-1.5" style={{ width: 90 }}>
                      <input
                        type="number"
                        min={0}
                        step="0.001"
                        placeholder="0.000"
                        value={f.dosis}
                        onChange={e => editarDosis(f.key, e.target.value)}
                        className="w-full px-2 py-1 rounded text-xs text-right font-semibold"
                        style={{
                          background: 'var(--surface-overlay)',
                          border: '1px solid var(--line)',
                          color: 'var(--ink)',
                          outline: 'none',
                        }}
                      />
                    </td>
                    <td className="px-3 py-2 text-right font-mono" style={{ color: 'var(--ink-muted)', width: 100 }}>{fmtNum(f.cantidad_boom)}</td>
                    <td className="px-3 py-2 text-right font-mono" style={{ color: 'var(--ink-muted)', width: 100 }}>{fmtNum(f.cantidad_fraccion)}</td>
                    <td className="px-3 py-2 text-right font-semibold font-mono" style={{ color: 'var(--ink)', width: 100 }}>{fmtNum(f.cantidad_aplicar)}</td>
                    <td className="px-3 py-2 text-center text-[11px]" style={{ color: 'var(--ink-muted)', width: 70 }}>{f.reingreso || '—'}</td>
                    <td className="px-3 py-2 text-center text-[11px]" style={{ color: 'var(--ink-muted)', width: 60 }}>{f.latencia || '—'}</td>
                    <td className="px-2 py-2 text-center" style={{ width: 36 }}>
                      <button onClick={() => eliminarFila(f.key)} style={{ color: 'var(--ink-faint)' }} className="hover:text-red-400 transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-3 sm:hidden">
            {filas.map((f) => (
              <article key={f.key} className="rounded-lg border p-3" style={{ background: 'var(--surface-overlay)', borderColor: 'var(--line)' }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono text-xs font-bold" style={{ color: 'var(--accent-main)' }}>{f.articulo}</div>
                    <div className="mt-1 text-sm font-semibold leading-snug" style={{ color: 'var(--ink)' }}>{f.nombre_producto}</div>
                    <div className="mt-1 text-xs" style={{ color: 'var(--ink-faint)' }}>
                      {f.medida || '—'}{f.reingreso ? ` · Reingreso ${f.reingreso}` : ''}{f.latencia ? ` · Latencia ${f.latencia}` : ''}
                    </div>
                  </div>
                  <button onClick={() => eliminarFila(f.key)} className="shrink-0 rounded p-2" style={{ color: '#f87171', background: 'rgba(248,113,113,0.08)' }}>
                    <Trash2 size={15} />
                  </button>
                </div>
                <div className="mt-3">
                  <label className={labelCls}>Dosis</label>
                  <input
                    type="number"
                    min={0}
                    step="0.001"
                    placeholder="0.000"
                    value={f.dosis}
                    onChange={e => editarDosis(f.key, e.target.value)}
                    className="w-full rounded px-3 py-2 text-right text-base font-semibold"
                    style={{
                      background: 'var(--surface-raised)',
                      border: '1px solid var(--line)',
                      color: 'var(--ink)',
                      outline: 'none',
                    }}
                  />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <InfoField label="Boom" value={fmtNum(f.cantidad_boom)} />
                  <InfoField label="Fracción" value={fmtNum(f.cantidad_fraccion)} />
                  <InfoField label="Aplicar" value={fmtNum(f.cantidad_aplicar)} accent />
                </div>
              </article>
            ))}
          </div>
          </>
        )}
      </div>

      {/* ── Acciones ── */}
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
        <button className={btnSecondary} onClick={() => {
          setAplicacionId(''); setBoomId(''); setVolumen(''); setTratamiento('');
          setSeleccion(null); setFilas([]); setOk(''); setError('');
        }}>Cancelar</button>
        <button className={btnPrimary} disabled={guardando} onClick={guardar}>
          {guardando ? <><Loader2 size={13} className="inline animate-spin mr-1.5" />Guardando…</> : 'Generar Boleta'}
        </button>
      </div>

      {/* Modales */}
      {showModalLote && (
        <ModalLoteBloques
          empresaId={empresaId}
          onClose={() => setShowModalLote(false)}
          onConfirm={sel => { setSeleccion(sel); setShowModalLote(false); }}
        />
      )}
      {showModalProducto && (
        <ModalBuscarProducto
          empresaId={empresaId}
          onClose={() => setShowModalProducto(false)}
          onSelect={agregarProducto}
        />
      )}
    </div>
  );
}

// ─── Sub-componentes menores ──────────────────────────────────────────────────

function InfoField({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: 'var(--ink-faint)' }}>{label}</div>
      <div className="text-xs font-semibold" style={{ color: accent ? 'var(--accent-main)' : 'var(--ink)' }}>{value}</div>
    </div>
  );
}

function Th({ children, w, right }: { children?: React.ReactNode; w?: number; right?: boolean }) {
  return (
    <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap"
      style={{ color: 'var(--ink-faint)', width: w, textAlign: right ? 'right' : 'left' }}>
      {children}
    </th>
  );
}
